import { withDependencies, named, optional } from '@wix/thunderbolt-ioc'
import {
	CurrentRouteInfoSymbol,
	FeatureStateSymbol,
	IPageDidMountHandler,
	PageFeatureConfigSymbol,
} from '@wix/thunderbolt-symbols'
import type { AutoDisplayLightboxPageConfig, AutoDisplayLightboxFeatureState } from './types'
import { name } from './symbols'
import { ILightbox, LightboxSymbol } from 'feature-lightbox'
import { IFeatureState } from 'thunderbolt-feature-state'
import { ICurrentRouteInfo } from 'feature-router'

const getLocaleDate = (timeZone: string, dateValue: string = '') => {
	const [regionAndCity] = timeZone.split(' ') // City/Region (GMT+TIME)
	const date = dateValue ? new Date(dateValue) : new Date()
	return date.toLocaleString('en-US', { timeZone: regionAndCity })
}

const isCurrentDateInRange = (startDate: string, endDate: string, timeZone: string) => {
	const start = getLocaleDate(timeZone, startDate)
	const end = getLocaleDate(timeZone, endDate)
	const current = new Date(getLocaleDate(timeZone))

	return current >= new Date(start) && current <= new Date(end)
}

export const autoDisplayLightboxFactory = (
	{ triggerPageId, popupId, delay, startDate, endDate, timeZone }: AutoDisplayLightboxPageConfig,
	featureState: IFeatureState<AutoDisplayLightboxFeatureState>,
	currentRouteInfo: ICurrentRouteInfo,
	popupsFeature?: ILightbox
): IPageDidMountHandler => {
	return {
		pageDidMount() {
			if (!popupsFeature) {
				return
			}
			const popupsOpened = featureState.get()
			const isTargetPopupPage = popupsFeature.isLightbox(popupId)
			const isClosed = !popupsOpened || !popupsOpened[popupId]

			const hasSchedule = startDate && endDate && timeZone
			const isInSchedule = hasSchedule && isCurrentDateInRange(startDate, endDate, timeZone)
			const canOpenPopup = !hasSchedule || isInSchedule

			if (isTargetPopupPage && canOpenPopup && isClosed) {
				setTimeout(() => {
					const currentPageId = currentRouteInfo.getCurrentRouteInfo()?.pageId
					const isTriggerPageCurrentPage = triggerPageId === currentPageId

					if (isTriggerPageCurrentPage || triggerPageId === 'masterPage') {
						popupsFeature.open(popupId)
						featureState.update((state) => ({ ...state, [popupId]: true }))
					}
				}, delay * 1000)
			}
		},
	}
}

export const AutoDisplayLightbox = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(FeatureStateSymbol, name),
		CurrentRouteInfoSymbol,
		optional(LightboxSymbol),
	],
	autoDisplayLightboxFactory
)
